import { useMemo } from "react";
import { uniqBy, groupBy } from "lodash";
import { usePublicMediaAssets, useLoadAllAssets } from "#src/hooks";
import { BrandKitAsset, MediaAsset } from "pages/media/types";
import {
  getRowKey,
  getRowLabel,
} from "pages/media/components/storyboardEditor/helpers";
import { getPrettyFontName } from "pages/media/utils";
import { t } from "i18next";
import { sortFontVariants, sortFontFamilies } from "../fontSelect";
import {
  FONT_SOURCES,
  FONT_CATEGORIES,
  FontFamily,
  FontItem,
  FontCategory,
} from "../types";

const createFontFamilies = (items: FontItem[]): FontFamily[] => {
  const grouped = groupBy(items, "prettyName") as Record<string, FontItem[]>;
  //TODO refactor when backend will support variants natively
  return sortFontFamilies(
    Object.entries(grouped).map(([familyName, variants]) => {
      const sortedVariants = sortFontVariants(variants);
      const representativeVariant =
        sortedVariants.find((v) => v.weight?.toLowerCase() === "regular") ||
        sortedVariants.find((v) => v.weight?.toLowerCase() === "normal") ||
        sortedVariants.find((v) => v.weight?.toLowerCase() === "book") ||
        sortedVariants[0];

      return {
        familyName,
        variants: sortedVariants,
        representativeVariant,
      };
    })
  );
};

const createFontItems = <T extends BrandKitAsset | Partial<MediaAsset>>(
  fonts: T[],
  source: keyof typeof FONT_SOURCES
): FontItem[] => {
  return fonts
    ?.filter((font): font is NonNullable<typeof font> => font !== undefined)
    .map((font) => {
      const fullName = getRowLabel(font);
      const prettyName = getPrettyFontName(
        fullName,
        // keep id as fallback to not group fonts with no name
        (font as any).id ?? (font as any).asset_id //t("components.FontSelect.example")
      );
      // const familyName = prettyName
      // console.log(font, prettyName);
      let weight = fullName.replace(prettyName, "").trim();
      weight = weight
        .trim()
        .replace(/^[-_]+/, "")
        .replace(/[-_]+$/, "");
      weight = weight.replace(/\.(ttf|otf|woff|woff2)$/i, "");
      if (!weight) {
        weight = "Regular";
      } else {
        const weightMap: Record<string, string> = {
          normal: "Regular",
          regular: "Regular",
          book: "Regular",
          medium: "Medium",
          semibold: "SemiBold",
          "semi-bold": "SemiBold",
          demibold: "SemiBold",
          bold: "Bold",
          extrabold: "ExtraBold",
          "extra-bold": "ExtraBold",
          ultrabold: "ExtraBold",
          black: "Black",
          heavy: "Black",
          light: "Light",
          thin: "Thin",
          ultralight: "Thin",
          "extra-light": "Thin",
        };

        const normalizedWeight = weightMap[weight.toLowerCase()] || weight;
        weight = normalizedWeight;
      }
      return {
        font,
        prettyName,
        fullName,
        key: getRowKey(font),
        source: FONT_SOURCES[source],
        weight,
        hasName: !!fullName,
      };
    });
};

interface UseFontGroupsProps {
  brandKitFonts: BrandKitAsset[];
  assetLibraryId: string;
  /** When true, the first category uses the "current font" label instead of "font catalog". */
  useCurrentFontLabel?: boolean;
  /** When true, only brand-kit fonts are exposed (catalog & library are hidden). */
  brandKitOnly?: boolean;
}

export const useFontGroups = ({
  brandKitFonts,
  assetLibraryId,
  useCurrentFontLabel = false,
  brandKitOnly = false,
}: UseFontGroupsProps) => {
  const { publicFonts } = usePublicMediaAssets();

  const {
    fonts: assetLibraryFonts,
    updateAllLoadedAssetsOptimistically,
    deleteAssetOptimistically,
  } = useLoadAllAssets({
    currentAssetLibraryId: assetLibraryId,
  });

  const fontCategories = useMemo(() => {
    const allFonts = [
      ...createFontItems(uniqBy(brandKitFonts, "asset_id"), "BRANDKIT"),
      ...(brandKitOnly
        ? []
        : [
            ...createFontItems(
              publicFonts.filter(
                (publicFont) =>
                  !brandKitFonts.some(
                    (brandkitFont) => publicFont.id === brandkitFont.asset_id
                  )
              ),
              "PUBLIC"
            ),
            ...createFontItems(uniqBy(assetLibraryFonts, "id"), "LIBRARY"),
          ]),
    ];

    const brandkitFonts = allFonts.filter(
      (item) => item.source === FONT_SOURCES.BRANDKIT
    );
    const catalogFonts = allFonts.filter(
      (item) => item.source === FONT_SOURCES.PUBLIC
    );
    const libraryFonts = allFonts.filter(
      (item) => item.source === FONT_SOURCES.LIBRARY
    );

    const categories: FontCategory[] = [];

    if (brandkitFonts.length > 0) {
      categories.push({
        categoryName: useCurrentFontLabel
          ? t("components.FontSelect.currentFont")
          : t("components.FontSelect.brandkitFonts"),
        categoryType: FONT_CATEGORIES.BRANDKIT_FONTS,
        families: createFontFamilies(brandkitFonts),
      });
    }
    if (libraryFonts.length > 0) {
      categories.push({
        categoryName: t("components.FontSelect.libraryFonts"),
        categoryType: FONT_CATEGORIES.LIBRARY_FONTS,
        families: createFontFamilies(libraryFonts),
      });
    }

    if (catalogFonts.length > 0) {
      categories.push({
        categoryName: t("components.FontSelect.fontCatalog"),
        categoryType: FONT_CATEGORIES.FONT_CATALOG,
        families: createFontFamilies(catalogFonts),
      });
    }

    return categories;
  }, [
    brandKitFonts,
    publicFonts,
    assetLibraryFonts,
    // fontArraysKey,
    useCurrentFontLabel,
    brandKitOnly,
  ]);

  const allFontItems = useMemo(
    () =>
      fontCategories.flatMap((category) =>
        category.families.flatMap((family) => family.variants)
      ),
    [fontCategories]
  );

  return {
    fontCategories,
    allFontItems,
    updateFontsOptimistically: updateAllLoadedAssetsOptimistically,
    deleteFontOptimistically: deleteAssetOptimistically,
  };
};
